import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface YouTubePreviewProps {
  videoUrl: string;
  title?: string;
}

const VideoContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  margin: 20px 0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  background: rgba(15, 23, 42, 0.9);
`;

const VideoThumbnail = styled.div<{ backgroundImage: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background-image: url(${props => props.backgroundImage});
  background-size: cover;
  background-position: center bottom;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
  }
  
  &:hover::before {
    background: rgba(0, 0, 0, 0.1);
  }
`;

const PlayButton = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: rgba(255, 0, 0, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 0, 0, 1);
    transform: translate(-50%, -50%) scale(1.1);
  }
  
  &::before {
    content: '';
    width: 0;
    height: 0;
    border-left: 25px solid white;
    border-top: 15px solid transparent;
    border-bottom: 15px solid transparent;
    margin-left: 5px;
  }
`;

const VideoFrame = styled.iframe`
  width: 100%;
  aspect-ratio: 16 / 9;
  border: none;
`;

const VideoTitle = styled.h4`
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  padding: 16px 20px 8px;
`;

const VideoMeta = styled.div`
  padding: 0 20px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ViewOnYouTube = styled.a`
  color: ${({ theme }) => theme.colors.accent};
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    text-decoration: underline;
  }
  
  &::after {
    content: '↗';
    font-size: 16px;
  }
`;

const DurationBadge = styled.span`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  position: absolute;
  bottom: 12px;
  right: 12px;
`;

const YouTubePreview: React.FC<YouTubePreviewProps> = ({ videoUrl, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Extract video ID from YouTube URL
  const getVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };
  
  // Extract timestamp from URL if present
  const getStartTime = (url: string) => {
    const match = url.match(/[?&]t=(\d+)/);
    return match ? parseInt(match[1]) : 0;
  };
  
  const videoId = getVideoId(videoUrl);
  const startTime = getStartTime(videoUrl);
  
  if (!videoId) {
    return null;
  }
  
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}${startTime ? `?start=${startTime}` : ''}`;
  
  const handlePlay = () => {
    setIsPlaying(true);
  };
  
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  return (
    <VideoContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {!isPlaying ? (
        <>
          <VideoThumbnail backgroundImage={thumbnailUrl} onClick={handlePlay}>
            <PlayButton
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            />
            {startTime > 0 && (
              <DurationBadge>
                Starts at {formatDuration(startTime)}
              </DurationBadge>
            )}
          </VideoThumbnail>
          {title && <VideoTitle>{title}</VideoTitle>}
          <VideoMeta>
            <ViewOnYouTube href={videoUrl} target="_blank" rel="noopener noreferrer">
              Watch on YouTube
            </ViewOnYouTube>
          </VideoMeta>
        </>
      ) : (
        <VideoFrame
          src={embedUrl}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title={title || "YouTube Video"}
        />
      )}
    </VideoContainer>
  );
};

export default YouTubePreview;